import React from 'react';
import { Card } from '@/components/ds';
import { ShieldCheck, AlertTriangle, Clock } from 'lucide-react';

/**
 * AgencyCompliance — high-level roll-up of agent lifecycle statuses and
 * appointment health across this agency. Purely aggregated from the data
 * already fetched by the orchestrator — no additional API call.
 */
const Cell = ({ label, value, tone = 'neutral', icon: Icon }) => {
  const tones = {
    success: { fg: '#15794a', bg: '#e9f6ef' },
    warning: { fg: 'var(--kr-gold-600)', bg: 'var(--kr-gold-50)' },
    danger:  { fg: '#a11b1b', bg: '#fdecec' },
    neutral: { fg: 'var(--kr-navy-800)', bg: '#f6f7fa' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
           style={{ background: t.bg, color: t.fg }}>
        <Icon size={17} />
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--kr-subtle)' }}>{label}</div>
        <div className="text-xl font-heading font-semibold tabular-nums" style={{ color: 'var(--kr-navy-900)' }}>{value}</div>
      </div>
    </div>
  );
};

export const AgencyCompliance = ({ agents = [], appointments = [] }) => {
  const active = agents.filter((a) => a.agent_status === 'active').length;
  const onboarding = agents.filter((a) => a.agent_status === 'onboarding').length;
  const inactive = agents.filter((a) => a.agent_status === 'terminated' || a.agent_status === 'inactive').length;
  const pendingAppts = appointments.filter((a) => a.appointment_status === 'Pending').length;

  return (
    <Card
      icon={ShieldCheck}
      title="Compliance Overview"
      description="Agent lifecycle roll-up and pending carrier appointments."
      testId="agency-compliance"
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Cell label="Active Agents" value={active} icon={ShieldCheck} tone={active > 0 ? 'success' : 'neutral'} />
        <Cell label="Onboarding" value={onboarding} icon={Clock} tone={onboarding > 0 ? 'warning' : 'neutral'} />
        <Cell label="Inactive" value={inactive} icon={AlertTriangle} tone={inactive > 0 ? 'danger' : 'neutral'} />
        <Cell label="Pending Appointments" value={pendingAppts} icon={Clock} tone={pendingAppts > 0 ? 'warning' : 'neutral'} />
      </div>
    </Card>
  );
};

export default AgencyCompliance;
